import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function formatPrice(p) {
  if (p == null) return '—';
  return typeof p === 'number' ? p.toFixed(5) : parseFloat(p).toFixed(5);
}

function formatPnl(pnl) {
  if (pnl == null) return '—';
  const sign = pnl > 0 ? '+' : '';
  return `${sign}${pnl.toFixed(2)}`;
}

export default function PositionsList({
  openPositions,
  pendingOrders,
  closedTrades,
  currentPrice,
  sessionId,
  onClosePosition,
  onModifyPosition,
  onCancelPendingOrder,
  forceTab,
}) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(forceTab || 'open');
  useEffect(() => { if (forceTab) setActiveTab(forceTab); }, [forceTab]);
  const [editingId, setEditingId] = useState(null);
  const [editSl, setEditSl] = useState('');
  const [editTp, setEditTp] = useState('');

  const startEdit = (pos) => {
    setEditingId(pos.id);
    setEditSl(pos.stop_loss != null ? String(pos.stop_loss) : '');
    setEditTp(pos.take_profit != null ? String(pos.take_profit) : '');
  };

  const saveEdit = (positionId) => {
    onModifyPosition(positionId, {
      stopLoss: editSl ? parseFloat(editSl) : null,
      takeProfit: editTp ? parseFloat(editTp) : null,
    });
    setEditingId(null);
  };

  const computeUnrealized = (pos) => {
    if (!currentPrice) return 0;
    const diff = pos.direction === 'buy'
      ? currentPrice - pos.entry_price
      : pos.entry_price - currentPrice;
    return Math.round(diff * pos.volume * 100) / 100;
  };

  const tabs = [
    { key: 'open', label: `Open (${openPositions.length})` },
    { key: 'pending', label: `Pending (${pendingOrders.length})` },
    { key: 'closed', label: `Closed (${closedTrades.length})` },
  ];

  return (
    <div className="positions-list">
      <div className="positions-tabs">
        {tabs.map((tab) => (
          <button key={tab.key} className={`positions-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="positions-body">
        {activeTab === 'open' && (
          openPositions.length === 0 ? (
            <div className="positions-empty">No open positions</div>
          ) : (
            <div className="pos-cards">
              {openPositions.map((pos) => {
                const pnl = computeUnrealized(pos);
                const pnlClass = pnl > 0 ? 'pnl-positive' : pnl < 0 ? 'pnl-negative' : '';
                const isEditing = editingId === pos.id;
                return (
                  <div key={pos.id || pos.clientId} className="pos-card">
                    <div className="pos-card-top">
                      <span className={`dir-badge dir-${pos.direction}`}>{pos.direction.toUpperCase()}</span>
                      <span className="pos-vol">{pos.volume}</span>
                      <span className="pos-entry">{formatPrice(pos.entry_price)}</span>
                      <span className={`pos-pnl ${pnlClass}`}>{formatPnl(pnl)}</span>
                    </div>
                    {isEditing ? (
                      <div className="pos-card-edit">
                        <div className="pos-edit-row">
                          <label>SL</label>
                          <input type="number" step="any" value={editSl} onChange={(e) => setEditSl(e.target.value)} />
                          <label>TP</label>
                          <input type="number" step="any" value={editTp} onChange={(e) => setEditTp(e.target.value)} />
                        </div>
                        <div className="pos-edit-btns">
                          <button className="pos-btn pos-btn-save" onClick={() => saveEdit(pos.id)}>Save</button>
                          <button className="pos-btn" onClick={() => setEditingId(null)}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="pos-card-meta">
                          <span>SL: {formatPrice(pos.stop_loss)}</span>
                          <span>TP: {formatPrice(pos.take_profit)}</span>
                          {pos.trailing_distance && <span>Trail: {pos.trailing_distance}</span>}
                        </div>
                        {pos.id && (
                          <div className="pos-card-actions">
                            <button className="pos-btn pos-btn-close" onClick={() => onClosePosition(pos.id)}>Close</button>
                            {pos.volume > 0.02 && (
                              <>
                                <button className="pos-btn pos-btn-partial" onClick={() => {
                                  const vol = Math.round(pos.volume * 0.5 * 10000) / 10000;
                                  if (vol > 0) onClosePosition(pos.id, 'partial', vol);
                                }}>Close 50%</button>
                              </>
                            )}
                            <button className="pos-btn" onClick={() => startEdit(pos)}>Modify</button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}

        {activeTab === 'pending' && (
          pendingOrders.length === 0 ? (
            <div className="positions-empty">No pending orders</div>
          ) : (
            <div className="pos-cards">
              {pendingOrders.map((order) => (
                <div key={order.clientId} className="pos-card">
                  <div className="pos-card-top">
                    <span className="pos-type">{order.order_type}</span>
                    <span className={`dir-badge dir-${order.direction}`}>{order.direction.toUpperCase()}</span>
                    <span className="pos-entry">@ {formatPrice(order.price)}</span>
                    <span className="pos-vol">{order.volume}</span>
                  </div>
                  <div className="pos-card-meta">
                    <span>SL: {formatPrice(order.stop_loss)}</span>
                    <span>TP: {formatPrice(order.take_profit)}</span>
                  </div>
                  <div className="pos-card-actions">
                    <button className="pos-btn pos-btn-close" onClick={() => onCancelPendingOrder(order.clientId)}>Cancel</button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {activeTab === 'closed' && (
          closedTrades.length === 0 ? (
            <div className="positions-empty">No closed trades</div>
          ) : (
            <div className="pos-cards">
              {[...closedTrades].reverse().map((trade, i) => {
                const pnlClass = trade.pnl > 0 ? 'pnl-positive' : trade.pnl < 0 ? 'pnl-negative' : '';
                return (
                  <div key={trade.id || i} className="pos-card pos-card-clickable"
                    onClick={() => trade.id && sessionId && navigate(`/journal/trade/${sessionId}/${trade.id}`)}>
                    <div className="pos-card-top">
                      <span className={`dir-badge dir-${trade.direction}`}>{trade.direction.toUpperCase()}</span>
                      <span className="pos-vol">{trade.volume}</span>
                      <span className="pos-entry">{formatPrice(trade.entry_price)} → {formatPrice(trade.exit_price)}</span>
                      <span className={`pos-pnl ${pnlClass}`}>{formatPnl(trade.pnl)}</span>
                    </div>
                    <div className="pos-card-meta">
                      <span className="pos-reason">{trade.exit_reason}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
}
